(function () {
  'use strict';

  const BRAND = Object.freeze({
    fontHeading: '"Montserrat", sans-serif',
    fontWeightHeading: '400',
    colorText: '#001135',
    colorHover: '#230051',
    colorBorder: '#e2e4e8',
    accent: '#001135',
    tabStyle: 'underline',
    radius: '999px',
    tabTextTransform: 'none',
    tabLetterSpacing: '0',
  });

  const SELECTORS = Object.freeze({
    openButton: '.js-size-chart-open',
    closeButton: '.js-size-chart-close',
    container: '.js-size-chart-container',
    tableWrapper: '.rte__table-wrapper',
    table: '.size-table',
  });

  const CATEGORY_RULES = Object.freeze([
    { match: /bottoms/i, slug: 'bottoms', keywords: [/trunk/i, /brief/i, /boxer/i, /short/i] },
    { match: /socks?/i, slug: 'socks', keywords: [/sock/i] },
    { match: /singlet/i, slug: 'singlets', keywords: [/singlet/i, /tank/i] },
    { match: /.*/, slug: 'general', keywords: [] },
  ]);

  const initializedContainers = new WeakSet();
  let activeContainer = null;
  let lastFocusedElement = null;

  function slugFor(title) {
    return (CATEGORY_RULES.find((rule) => rule.match.test(title)) || CATEGORY_RULES.at(-1)).slug;
  }

  function shortLabel(title) {
    return title
      .replace(/^Men'?s\s*/i, '')
      .replace(/Size Guide$/i, '')
      .trim() || 'General';
  }

  function guessDefaultSlug() {
    const productContext = [
      document.querySelector('h1')?.textContent || '',
      document.title || '',
      window.location.pathname || '',
    ].join(' ');

    const match = CATEGORY_RULES.find((rule) => {
      return rule.slug !== 'general' && rule.keywords.some((keyword) => keyword.test(productContext));
    });
    return match ? match.slug : 'bottoms';
  }

  function injectStyles() {
    if (document.getElementById('sgt-tabs-style')) return;

    const activeTabCss = BRAND.tabStyle === 'pill'
      ? `
        .sgt-tab{padding:6px 16px;border-radius:${BRAND.radius};}
        .sgt-tab[aria-selected="true"]{background:${BRAND.accent};color:#fff;}
      `
      : `
        .sgt-tab{padding:0 0 10px;border-bottom:2px solid transparent;}
        .sgt-tab[aria-selected="true"]{border-bottom-color:${BRAND.accent};color:${BRAND.colorText};}
      `;

    const style = document.createElement('style');
    style.id = 'sgt-tabs-style';
    style.textContent = `
      .sgt-tabs{
        display:flex;
        gap:${BRAND.tabStyle === 'pill' ? '10px' : '20px'};
        margin:4px 0 20px;
        overflow-x:auto;
        ${BRAND.tabStyle === 'pill' ? '' : `border-bottom:1px solid ${BRAND.colorBorder};`}
      }
      .sgt-tab{
        flex:none;
        border:0;
        background:none;
        appearance:none;
        color:${BRAND.colorText};
        cursor:pointer;
        font-family:${BRAND.fontHeading};
        font-size:13px;
        font-weight:${BRAND.fontWeightHeading};
        letter-spacing:${BRAND.tabLetterSpacing};
        text-transform:${BRAND.tabTextTransform};
        white-space:nowrap;
      }
      .sgt-tab:hover{color:${BRAND.colorHover};}
      .sgt-tab:focus-visible{outline:2px solid ${BRAND.accent};outline-offset:3px;}
      ${activeTabCss}
      .sgt-panel[hidden]{display:none!important;}
      .sgt-panel table.size-table{margin-top:0;}
    `;
    document.head.appendChild(style);
  }

  function getEntries(container) {
    return Array.from(container.querySelectorAll(SELECTORS.tableWrapper)).map((wrapper, index) => {
      const table = wrapper.querySelector(SELECTORS.table);
      const titleElement = table?.querySelector('th h4,td h4,h4');
      const title = (titleElement?.textContent || 'Size Guide').trim();
      const slug = slugFor(title);

      titleElement?.closest('tr')?.remove();
      wrapper.classList.add('sgt-panel');
      wrapper.id = `sgt-panel-${slug}-${index}`;
      wrapper.setAttribute('role', 'tabpanel');
      wrapper.setAttribute('tabindex', '0');

      return { wrapper, title, slug, index };
    });
  }

  function createTab(entry) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'sgt-tab';
    button.id = `sgt-tab-${entry.slug}-${entry.index}`;
    button.dataset.slug = entry.slug;
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-controls', entry.wrapper.id);
    button.setAttribute('aria-selected', 'false');
    button.setAttribute('tabindex', '-1');
    button.textContent = shortLabel(entry.title);
    entry.wrapper.setAttribute('aria-labelledby', button.id);
    return button;
  }

  function activate(container, requestedSlug, { focusTab = false } = {}) {
    const state = container.__sgtState;
    if (!state) return;

    const activeEntry = state.entries.find((entry) => entry.slug === requestedSlug)
      || state.entries.find((entry) => entry.slug === 'general')
      || state.entries[0];

    state.entries.forEach((entry) => {
      const isActive = entry === activeEntry;
      entry.wrapper.hidden = !isActive;
      const tab = state.tabs.find((candidate) => candidate.dataset.slug === entry.slug);
      if (!tab) return;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive && focusTab) tab.focus();
    });
  }

  function onTabKeydown(event, container) {
    const state = container.__sgtState;
    if (!state) return;

    const currentIndex = state.tabs.indexOf(event.currentTarget);
    let nextIndex = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % state.tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + state.tabs.length) % state.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = state.tabs.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    activate(container, state.tabs[nextIndex].dataset.slug, { focusTab: true });
  }

  function buildTabs(container) {
    if (initializedContainers.has(container)) return true;

    const entries = getEntries(container);
    if (!entries.length) return false;

    const tablist = document.createElement('div');
    tablist.className = 'sgt-tabs';
    tablist.setAttribute('role', 'tablist');
    tablist.setAttribute('aria-label', 'Size guide categories');

    const tabs = entries.map((entry) => {
      const tab = createTab(entry);
      tab.addEventListener('click', () => activate(container, entry.slug));
      tab.addEventListener('keydown', (event) => onTabKeydown(event, container));
      tablist.appendChild(tab);
      return tab;
    });

    entries[0].wrapper.parentElement.insertBefore(tablist, entries[0].wrapper);
    container.__sgtState = { entries, tablist, tabs };
    initializedContainers.add(container);
    injectStyles();
    return true;
  }

  function openModal(trigger) {
    const container = document.querySelector(SELECTORS.container);
    if (!container) return;

    lastFocusedElement = trigger || document.activeElement;
    activeContainer = container;
    buildTabs(container);
    document.body.classList.add('size-chart-is-open');
    activate(container, guessDefaultSlug());

    window.requestAnimationFrame(() => {
      const activeTab = container.querySelector('.sgt-tab[aria-selected="true"]');
      activeTab?.focus();
    });
  }

  function closeModal() {
    document.body.classList.remove('size-chart-is-open');
    activeContainer = null;
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest(SELECTORS.openButton);
    if (openButton) openModal(openButton);
    if (event.target.closest(SELECTORS.closeButton)) closeModal();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeContainer) closeModal();
  });
})();
